import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../lib/supabaseClient';
import { updatePup } from '../services/pupsStore';

const isUnavailableStatus = (status) => {
  const s = String(status || '').trim().toLowerCase();
  return s === 'sold' || s === 'not available' || s === 'unavailable' || s === 'out of stock';
};

export default function PupPage({ brand, pets, contact, cartPetIds, onAddToCart, onRemoveFromCart, onPetUpdated }) {
  const { id } = useParams();
  const navigate = useNavigate();

  const pet = useMemo(() => pets.find((p) => p.id === id) || null, [pets, id]);
  const [activeIdx, setActiveIdx] = useState(0);

  const [session, setSession] = useState(null);
  const [adminError, setAdminError] = useState('');
  const [adminBusy, setAdminBusy] = useState(false);
  const [draftStatus, setDraftStatus] = useState('');
  const [draftFee, setDraftFee] = useState('');

  const canAdmin =
    Boolean(session?.user?.email) &&
    (!process.env.REACT_APP_ADMIN_EMAIL ||
      String(process.env.REACT_APP_ADMIN_EMAIL).trim().toLowerCase() === String(session.user.email).toLowerCase());

  useEffect(() => {
    if (!supabase) return undefined;
    supabase.auth.getSession().then(({ data }) => setSession(data?.session || null));
    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => setSession(next));
    return () => sub?.subscription?.unsubscribe?.();
  }, []);

  useEffect(() => {
    setActiveIdx(0);
  }, [id]);

  useEffect(() => {
    if (!pet) return;
    setDraftStatus(pet.status || 'Available');
    setDraftFee(typeof pet.price === 'number' ? String(pet.price) : '');
  }, [pet]);

  if (!pet) {
    return (
      <div className="page pup-page">
        <div className="pup-page-inner">
          <div className="pup-page-card">
            <h1>Pup not found</h1>
            <p>This pup may have been removed or is no longer available.</p>
            <Link className="pup-back" to="/catalogue">
              Back to catalogue
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const images = Array.isArray(pet.images) ? pet.images : [];
  const activeSrc = images[activeIdx] || images[0] || null;
  const unavailable = isUnavailableStatus(pet.status);

  const openWhatsApp = () => {
    const fee = typeof pet.price === 'number' ? ` (Adoption fee: $${pet.price})` : '';
    const lines = [
      `Hi ${brand}!`,
      '',
      `I’m interested in ${pet.title}.${fee}`,
      'Please confirm availability and share next steps (pickup/shipping).'
    ];
    window.open(`${contact.whatsappUrl}?text=${encodeURIComponent(lines.join('\n'))}`, '_blank', 'noreferrer');
  };

  const saveAdmin = async () => {
    if (!canAdmin) return;
    setAdminBusy(true);
    setAdminError('');
    try {
      const nextPrice = draftFee.trim() ? Number(draftFee.trim()) : null;
      const updated = await updatePup(pet.id, { status: draftStatus, price: nextPrice });
      onPetUpdated?.(pet.id, updated);
    } catch (e) {
      setAdminError(e?.message || 'Failed to update pup.');
    } finally {
      setAdminBusy(false);
    }
  };

  return (
    <div className="page pup-page">
      <div className="pup-page-inner">
        <div className="pup-breadcrumbs">
          <button className="pup-back" type="button" onClick={() => navigate(-1)}>
            ← Back
          </button>
          <Link className="pup-link" to="/catalogue">
            Catalogue
          </Link>
          <span className="pup-sep">/</span>
          <span className="pup-current">{pet.title}</span>
        </div>

        <div className="pup-layout">
          <motion.div
            className="pup-media"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="pup-main">
              {activeSrc ? <img src={activeSrc} alt={pet.title} /> : <div className="photo-placeholder" aria-hidden="true" />}
              <div className="pup-badges" aria-hidden="true">
                <span className={`status-badge ${unavailable ? 'is-off' : 'is-on'}`}>
                  {unavailable ? 'Not available' : pet.status || 'Available'}
                </span>
              </div>
            </div>

            {images.length > 1 ? (
              <div className="pup-thumbs" aria-label="Choose photo">
                {images.map((src, idx) => (
                  <button
                    key={`${pet.id}-img-${idx}`}
                    type="button"
                    className={`pup-thumb ${idx === activeIdx ? 'is-active' : ''}`}
                    onClick={() => setActiveIdx(idx)}
                    aria-label={`Show photo ${idx + 1}`}
                  >
                    <img src={src} alt="" loading="lazy" />
                  </button>
                ))}
              </div>
            ) : null}
          </motion.div>

          <motion.div
            className="pup-info"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05 }}
          >
            <div className="pup-title-row">
              <h1>{pet.title}</h1>
              {typeof pet.price === 'number' ? <div className="pup-fee">${pet.price}</div> : null}
            </div>
            <div className="pup-sub">
              {pet.breed ? `${pet.breed} • ` : ''}
              {pet.sex || '—'}
            </div>

            <dl className="pup-specs">
              <div className="pup-spec">
                <dt>Status</dt>
                <dd>{unavailable ? 'Not available' : pet.status || 'Available'}</dd>
              </div>
              <div className="pup-spec">
                <dt>Age</dt>
                <dd>{pet.age || 'Ask for details'}</dd>
              </div>
              <div className="pup-spec">
                <dt>Weight</dt>
                <dd>{pet.weight || 'Ask for details'}</dd>
              </div>
              <div className="pup-spec">
                <dt>Size</dt>
                <dd>{pet.size || 'Ask for details'}</dd>
              </div>
            </dl>

            {pet.description ? <p className="pup-desc">{pet.description}</p> : null}

            <div className="pup-actions">
              <button className="pup-primary" type="button" onClick={openWhatsApp}>
                Message on WhatsApp
              </button>
              {!unavailable ? (
                cartPetIds.includes(pet.id) ? (
                  <button className="pup-secondary" type="button" onClick={onRemoveFromCart(pet.id)}>
                    Remove from enquiry
                  </button>
                ) : (
                  <button className="pup-secondary" type="button" onClick={onAddToCart(pet.id)}>
                    Add to enquiry
                  </button>
                )
              ) : (
                <div className="pup-unavailable-note">This pup is currently not available. Message us for alternatives.</div>
              )}
            </div>

            {canAdmin ? (
              <div className="pup-admin">
                <div className="pup-admin-title">Admin controls</div>
                <div className="pup-admin-grid">
                  <label className="pup-admin-field">
                    <span>Status</span>
                    <select value={draftStatus} onChange={(e) => setDraftStatus(e.target.value)}>
                      <option>Available</option>
                      <option>Not available</option>
                      <option>Sold</option>
                    </select>
                  </label>
                  <label className="pup-admin-field">
                    <span>Adoption fee</span>
                    <input value={draftFee} onChange={(e) => setDraftFee(e.target.value)} inputMode="numeric" />
                  </label>
                </div>
                {adminError ? <div className="pup-admin-error">{adminError}</div> : null}
                <button className="pup-admin-save" type="button" onClick={saveAdmin} disabled={adminBusy}>
                  {adminBusy ? 'Saving…' : 'Save'}
                </button>
              </div>
            ) : null}
          </motion.div>
        </div>
      </div>
    </div>
  );
}

